// src/helpers/fechas.js
// Helper robusto para convertir cualquier representación de fecha que viene
// de Firestore a un Date de JS.
//
// Maneja todas estas formas (necesario porque el restore desde prod puede
// dejar Timestamps como strings ISO):
//   - Firestore Timestamp (con .toDate())
//   - Date instance
//   - String ISO 8601 ("2025-05-13T10:30:00.000Z")
//   - { seconds, nanoseconds } o { _seconds, _nanoseconds }
//   - Número (epoch ms o epoch s)
//   - undefined / null

const ISO_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;

export function toJsDate(value) {
  if (value == null) return null;

  // Firestore Timestamp del SDK web
  if (typeof value?.toDate === "function") {
    try {
      const d = value.toDate();
      return Number.isNaN(d?.getTime?.()) ? null : d;
    } catch {
      return null;
    }
  }

  // Date instance
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  // String
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return null;

    // Formato dd/mm/yyyy o dd-mm-yyyy (común en imports MX)
    const dmy = trimmed.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2,4})/);
    if (dmy) {
      let [, d, m, y] = dmy;
      if (y.length === 2) y = "20" + y;
      const dy = Number(y), dm = Number(m) - 1, dd = Number(d);
      // Validación básica para evitar parseos absurdos
      if (dy >= 1900 && dy <= 2100 && dm >= 0 && dm <= 11 && dd >= 1 && dd <= 31) {
        const date = new Date(dy, dm, dd);
        if (!Number.isNaN(date.getTime())) return date;
      }
    }

    // HOTFIX v1.1.5: "yyyy-mm-dd" puro debe interpretarse como LOCAL.
    // `new Date("2026-06-04")` en ES2015+ se trata como UTC midnight, lo
    // cual en MX (UTC-6) da "3 jun 18:00 local" → cualquier consumidor que
    // luego haga setHours(0,0,0,0) acaba con "3 jun 00:00 local" (off-by-one).
    // Si en cambio el string YA tiene componente de hora (T...) lo dejamos
    // pasar tal cual: new Date entiende ISO 8601 correctamente.
    const ymdPuro = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmed);
    if (ymdPuro) {
      const [, yy, mm, dd] = ymdPuro;
      const local = new Date(Number(yy), Number(mm) - 1, Number(dd));
      return Number.isNaN(local.getTime()) ? null : local;
    }

    // ISO 8601 completo, "yyyy-mm-dd HH:MM:SS" sin Z, o cualquier otro que Date() entienda
    const d = new Date(trimmed);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  // Número: epoch ms o s
  if (typeof value === "number") {
    if (!Number.isFinite(value)) return null;
    // Heurística: < 1e12 lo tratamos como segundos
    const ms = value < 1e12 ? value * 1000 : value;
    const d = new Date(ms);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  // Plain object
  if (typeof value === "object") {
    // 1) Formato Firestore serializado: seconds/_seconds (+ opcional nanos)
    const seconds = value.seconds ?? value._seconds;
    const nanoseconds = value.nanoseconds ?? value._nanoseconds ?? 0;
    if (seconds != null) {
      const ms = Number(seconds) * 1000 + Math.floor(Number(nanoseconds) / 1e6);
      const d = new Date(ms);
      if (!Number.isNaN(d.getTime())) return d;
    }

    // 2) Wrappers anidados comunes en algunos imports/restores:
    //    { value: '2025-05-13...' }, { date: '...' }, { iso: '...' }, etc.
    for (const k of ["value", "date", "_date", "iso", "isoString", "fecha", "raw"]) {
      if (value[k] != null && value[k] !== value) {
        const nested = toJsDate(value[k]);
        if (nested) return nested;
      }
    }
  }

  return null;
}

// Devuelve epoch ms o 0 si no se pudo parsear
export function toJsMs(value) {
  const d = toJsDate(value);
  return d ? d.getTime() : 0;
}

/**
 * Para el motor de cobranza: normaliza cualquier fecha "solo día" a
 * **local midnight del día UTC** que el Timestamp representa.
 *
 * Existen dos formas legacy de almacenamiento en Firestore:
 *   - UTC midnight       (bug viejo: `new Date("2026-05-30")` = 2026-05-30 00:00 UTC)
 *   - Local midnight MX  (escritura nueva: 2026-05-30 00:00 MX = 06:00 UTC mismo dia)
 *
 * Si el motor toma `toJsDate(...)` directamente y luego hace `setHours(0,0,0,0)`,
 * las fechas legacy quedan corridas a "ayer" local (porque 00:00 UTC en MX es
 * 18:00 del dia anterior). Eso provoca:
 *   - Dias vencidos +1
 *   - Aging movido al bucket mas viejo en cuentas borde
 *   - Recalculo de nextDueDate corrido (cascada)
 *
 * Esta funcion toma los componentes UTC del Date (que coinciden con el numero
 * de dia visible en formatFecha con timeZone UTC) y construye un Date local
 * a esa medianoche. Asi los calculos de aging quedan consistentes con lo que
 * el usuario ve.
 *
 * @param {*} value Cualquier representacion (Timestamp, Date, ISO string, etc.)
 * @returns {Date|null} Date a las 00:00:00 local del dia UTC, o null si no parsea.
 */
export function toFechaSoloLocal(value) {
  const d = toJsDate(value);
  if (!d) return null;
  // Tomamos el dia UTC (mismo numero que se muestra en formatFecha).
  const y = d.getUTCFullYear();
  const m = d.getUTCMonth();
  const day = d.getUTCDate();
  const local = new Date(y, m, day);
  local.setHours(0, 0, 0, 0);
  return local;
}

/**
 * Variante en milisegundos para math de aging.
 * @returns {number} ms o 0 si no parsea.
 */
export function toFechaSoloLocalMs(value) {
  const d = toFechaSoloLocal(value);
  return d ? d.getTime() : 0;
}

/**
 * Formatea una fecha (o "hoy" si no se pasa argumento) como "yyyy-mm-dd"
 * usando la zona LOCAL del browser (NO UTC).
 *
 * Esto reemplaza el patrón frágil `new Date().toISOString().slice(0, 10)`
 * que usaba UTC y producía resultados off-by-one en CDMX dependiendo de la
 * hora del día (típicamente daba el día siguiente despues de las 18:00 local,
 * o el día anterior en ciertas condiciones de borde con datos importados).
 *
 * Casos típicos de uso:
 *   - default de input type="date"
 *   - serialización de fecha solo-día a Firestore como string
 *   - búsqueda por día actual
 *
 * @param {Date} [date]  Date opcional. Si no se pasa, usa now.
 * @returns {string} "yyyy-mm-dd" en zona local
 */
export function toLocalISODate(date) {
  const d = date instanceof Date ? date : new Date();
  if (Number.isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/**
 * Atajo: "hoy" en zona local como "yyyy-mm-dd".
 * Útil para defaults de inputs y filtros "del día".
 */
export function hoyLocalISO() {
  return toLocalISODate(new Date());
}

// Formatea como dd/mm/yyyy (locale del navegador). Devuelve fallback si no
// hay fecha válida.
//
// IMPORTANTE: usa `timeZone: "UTC"` para evitar el shift de "un día menos"
// que aparece cuando una fecha "solo día" se guardó como UTC midnight (caso
// legacy de `new Date("yyyy-mm-dd")`) y se renderiza en zonas UTC-N. Como
// la app maneja fechas contractuales y de pago en MX (UTC-6), usar UTC
// preserva el día correcto tanto para fechas viejas (UTC midnight) como
// nuevas (local midnight = 06:00 UTC mismo día).
export function formatFecha(value, fallback = "-") {
  const d = toJsDate(value);
  if (!d) return fallback;
  try {
    return d.toLocaleDateString("es-MX", {
      timeZone: "UTC",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  } catch {
    return d.toLocaleDateString();
  }
}

// Formatea fechas "solo día sin hora" (contratos, pagos, promesas) usando
// la zona UTC para evitar el shift de un día menos cuando el Timestamp se
// guardó como UTC midnight (legacy) en lugar de local midnight (actual).
//
// Detalles: si la fecha guardada es "2026-05-30 00:00 UTC", en MX (UTC-6)
// `.toLocaleDateString()` muestra "29". Forzando `timeZone: "UTC"` siempre
// muestra "30" tanto para fechas legacy (UTC midnight) como para las
// nuevas (local midnight = 06:00 UTC mismo día).
export function formatFechaSolo(value, fallback = "-") {
  const d = toJsDate(value);
  if (!d) return fallback;
  try {
    return d.toLocaleDateString("es-MX", {
      timeZone: "UTC",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  } catch {
    return fallback;
  }
}

// Formato corto con hora.
export function formatFechaHora(value, fallback = "-") {
  const d = toJsDate(value);
  if (!d) return fallback;
  return `${d.toLocaleDateString()} ${d.toLocaleTimeString()}`;
}

/**
 * EST1-3: render defensivo de cualquier valor en JSX.
 *
 * React revienta cuando se intenta renderizar un objeto plano como hijo.
 * Esto pasa frecuentemente con Timestamps serializados (`{seconds, nanoseconds}`),
 * mapas anidados, refs sin resolver, etc. — particularmente despues de un
 * restore desde JSON.
 *
 * `safeRender(value)`:
 *   - string / number / boolean / null / undefined  -> los devuelve tal cual
 *     (React los renderiza sin problema)
 *   - Date / Timestamp / objetos `{seconds, ...}` -> los formatea como fecha
 *     local (dd/mm/yyyy) con `formatFecha`
 *   - Arrays -> los une con coma
 *   - Otros objetos -> devuelve el `fallback` (default "-")
 *
 * Uso recomendado: en cualquier render donde el campo podria venir en
 * formato inesperado por data legacy o restore. Ejemplo:
 *
 *   <td>{safeRender(row.fecha)}</td>
 */
export function safeRender(value, fallback = "-") {
  if (value === null || value === undefined) return fallback;
  if (typeof value === "string" || typeof value === "number") return value;
  if (typeof value === "boolean") return value ? "Sí" : "No";

  // Date / Timestamp / {seconds, nanoseconds} / strings ISO
  const asDate = toJsDate(value);
  if (asDate) return asDate.toLocaleDateString();

  if (Array.isArray(value)) {
    return value
      .map((v) => {
        if (v === null || v === undefined) return "";
        if (typeof v === "object") return JSON.stringify(v).slice(0, 30);
        return String(v);
      })
      .filter(Boolean)
      .join(", ");
  }

  // Objeto desconocido: fallback (no crashear)
  return fallback;
}
